import { useField, FieldConfig } from 'formik'
import React, { AllHTMLAttributes, ReactNode } from 'react'
import Select, { GroupBase, StylesConfig } from 'react-select'
import i18n from 'constants/strings'
import styles from './selectWTN.module.scss'
import formStyles from './styles/forms.module.scss'
import classNames from 'classnames'

const customStyles: StylesConfig<any, false, GroupBase<any>> = {
  clearIndicator: (provided, state) => ({
    ...provided,
  }),
  container: (provided, state) => ({
    ...provided,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
  }),
  group: (provided, state) => ({
    ...provided,
  }),
  groupHeading: (provided, state) => ({
    ...provided,
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
  }),
  input: (provided, state) => ({
    ...provided,
    margin: 0,
    padding: 0,
    height: '26px',
    color: '#16234c',
    backgroundColor: 'transparent',
  }),
  loadingIndicator: (provided, state) => ({
    ...provided,
  }),
  loadingMessage: (provided, state) => ({
    ...provided,
  }),
  menu: (provided, state) => ({
    ...provided,
    zIndex: 2,
    marginTop: '4px',
    paddingTop: '6px',
    paddingBottom: '6px',
    borderRadius: '8px',
  }),
  menuList: (provided, state) => ({
    ...provided,
    paddingLeft: 0,
  }),
  menuPortal: (provided, state) => ({
    ...provided,
  }),
  multiValue: (provided, state) => ({
    ...provided,
  }),
  multiValueLabel: (provided, state) => ({
    ...provided,
  }),
  multiValueRemove: (provided, state) => ({
    ...provided,
  }),
  noOptionsMessage: (provided, state) => ({
    ...provided,
  }),
  placeholder: (provided, state) => ({
    ...provided,
    marginLeft: 0,
  }),
  singleValue: (provided, state) => ({
    ...provided,
    marginLeft: 0,
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '13px 40px 13px 24px',
    backgroundColor: 'transparent',
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    paddingLeft: '24px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    backgroundColor: state.isSelected ? '#003a70' : state.isFocused ? '#f6f7f9' : '#fff',
    '&:hover': {
      color: '#16234c',
      backgroundColor: '#f6f7f9',
    },
    '&:focus': {
      color: '#16234c',
      backgroundColor: '#f6f7f9',
    },
  }),
  control: (_, state) => ({
    cursor: state.isDisabled ? 'unset' : 'pointer',
    width: '100%',
    maxWidth: '100%',
    height: '52px',
    transition: 'all 0.6s ease-in, background-image 0.1s ease-out',
    textOverflow: 'ellipsis',
    color: state.isDisabled ? '#9da1af' : '#16234c',
    borderRadius: '8px',
    boxShadow: state.isFocused
      ? '0 0 5px 0 rgba(0, 58, 112, 0.3)'
      : '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    border: state.isFocused ? 'solid 1px #003a70' : 'solid 1px #dadce0',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.6',
    letterSpacing: '0.32px',
    textAlign: 'left',
    fontSize: '1rem',
    backgroundColor: state.isDisabled ? '#f6f7f9' : '#ffffff',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '24px',
    backgroundPosition: 'center right 6px',
    '&:hover': {
      border: 'solid 1px #8f99b7',
    },
  }),
}

interface Option {
  value: string
  label: string
}

type Props = AllHTMLAttributes<HTMLDivElement> &
  FieldConfig & {
    options: Option[]
    className?: string
    placeholderText?: string
    disabled?: boolean
    disableErrorMessage?: boolean
    isSearchable?: boolean
    onChangeCallback?: (newValue) => void
    renderErrorMessage?: (msg: string) => ReactNode
  }

const SelectWTN = ({
  options,
  className = '',
  placeholderText = i18n.t('strCommonUI.select.pleaseChoose'),
  disabled,
  disableErrorMessage = false,
  onChangeCallback,
  renderErrorMessage,
  ...props
}: Props) => {
  const [field, meta, helpers] = useField(props as any)

  const { setValue } = helpers

  return (
    <>
      <Select
        {...field}
        {...props}
        options={options}
        value={(options ? options.find(option => option.value === field.value) : '') as any}
        onChange={(option: Option | null) => {
          const val = option ? option.value : ''
          setValue(val)
          if (onChangeCallback) onChangeCallback(val)
        }}
        placeholder={placeholderText}
        className={classNames(styles.select, {
          [className]: className,
          [styles.error]: meta.error,
        })}
        classNamePrefix='select-wtn'
        styles={customStyles}
        isDisabled={disabled}
      />
      {!disableErrorMessage && meta.touched && meta.error && (
        <div className={formStyles.errorMessage}>
          {renderErrorMessage ? renderErrorMessage(meta.error) : meta.error}
        </div>
      )}
    </>
  )
}

export default SelectWTN
